/**
 * AdminUsersPage
 *
 * Admin view of MU users and their per-instance access. Supports the full lifecycle:
 *   - Lookup & create (verification gate via /api/admin/users/lookup → POST /api/admin/users)
 *   - Invite flow for LOCAL users: one-time set-password links (copy-paste, optionally emailed)
 *   - List with lifecycle state (Invited / Invite expired / Active / Archived) + lock indicator
 *   - Resend/revoke invites, reset-password links, switch LOCAL ↔ AZURE, archive/unarchive
 *   - Sync instance access (per-user action + "Sync access" for everyone): re-probes the live
 *     instances and grants/revokes access; unreachable instances are never treated as removals
 *
 * Admins never set or see a password — creation and resets hand out one-time links instead.
 */

import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  KeyRound,
  Loader2,
  Lock,
  MailX,
  MoreHorizontal,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";

import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import {
  OneTimeLinkDialog,
  OneTimeLinkPanel,
  type OneTimeLink,
} from "../components/OneTimeLinkDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { useToast } from "../lib/use-toast";
import {
  useArchiveUser,
  useChangeAuthType,
  useCreateUser,
  useResendInvite,
  useResetPassword,
  useRevokeInvite,
  useSyncAllUsers,
  useSyncUserInstances,
  useUnarchiveUser,
  useUserLookup,
  useUsers,
  type AdminUser,
  type AdminUserWithLink,
  type InstanceSyncEntry,
  type InstanceSyncSummary,
  type LookupField,
  type UserLookupResult,
} from "../lib/api-central-mu";

const LOOKUP_FIELDS: { value: LookupField; label: string; placeholder: string }[] = [
  { value: "email", label: "Email", placeholder: "user@email.com" },
  { value: "username", label: "Username", placeholder: "jdoe" },
  { value: "employeeId", label: "Employee ID", placeholder: "EMP-12345" },
];

/** Human-readable one-liner for a sync result, e.g. "Access granted on B QA. Access removed on A DEV." */
function describeSync(sync: InstanceSyncSummary): string {
  const names = (entries: InstanceSyncEntry[]) =>
    entries.map((e) => e.instanceName ?? `#${e.instanceId}`).join(", ");
  const parts: string[] = [];
  if (sync.added.length > 0) parts.push(`Access granted on ${names(sync.added)}.`);
  if (sync.removed.length > 0) parts.push(`Access removed on ${names(sync.removed)}.`);
  if (parts.length === 0) parts.push("No changes — access is up to date.");
  if (sync.errors.length > 0) {
    parts.push(`Couldn't reach ${names(sync.errors)}; access there was left unchanged.`);
  }
  return parts.join(" ");
}

export default function AdminUsersPage() {
  const [addOpen, setAddOpen] = useState(false);
  const { mutate: syncAll, isPending: syncingAll } = useSyncAllUsers();
  const { toast } = useToast();

  const onSyncAll = () =>
    syncAll(undefined, {
      onSuccess: (results) => {
        let added = 0;
        let removed = 0;
        let failed = 0;
        for (const r of results) {
          if (r.syncFailed) {
            failed += 1;
            continue;
          }
          added += r.sync?.added.length ?? 0;
          removed += r.sync?.removed.length ?? 0;
        }
        toast({
          title: "Instance access synced",
          description:
            `${results.length} user(s) checked — ${added} grant(s), ${removed} removal(s)` +
            (failed > 0 ? `, ${failed} user(s) failed to sync` : "") +
            ".",
        });
      },
    });

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader />
        <main className="p-6 lg:p-8 space-y-6">
          <Header />

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Manage users and their access to each registered Leucine instance.
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onSyncAll} disabled={syncingAll}>
                {syncingAll ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                Sync access
              </Button>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="size-4" />
                    Add user
                  </Button>
                </DialogTrigger>
                {addOpen ? <AddUserDialog onDone={() => setAddOpen(false)} /> : null}
              </Dialog>
            </div>
          </div>

          <UsersTable />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function Header() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Users className="size-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Verified user accounts and their per-instance access.
        </p>
      </div>
    </div>
  );
}

// =====================================================================================
// Users table
// =====================================================================================

function UsersTable() {
  const { data: users, isLoading, isError, refetch } = useUsers();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Couldn't load the user list.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="size-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No users yet</CardTitle>
          <CardDescription>
            Use "Add user" to look up a person on a connected instance and create their MU account.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Employee ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Access</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <UserRow key={u.id} user={u} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || "—";
  return (
    <TableRow className={user.state === "ARCHIVED" ? "opacity-60" : ""}>
      <TableCell>
        <div className="font-medium">{name}</div>
        <div className="text-xs text-muted-foreground">{user.email}</div>
      </TableCell>
      <TableCell className="text-sm">{user.username || "—"}</TableCell>
      <TableCell className="text-sm">{user.employeeId || "—"}</TableCell>
      <TableCell>
        <UserTypeBadge type={user.userType} />
      </TableCell>
      <TableCell className="text-sm">
        {user.userType === "DEFAULT" ? (
          <span className="text-muted-foreground">All (admin)</span>
        ) : user.instances && user.instances.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {user.instances.map((i) => (
              <Badge key={i.connectedServiceId} variant="secondary" className="font-normal">
                {i.serviceName ?? `#${i.connectedServiceId}`}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">No access</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <UserStateBadge state={user.state} />
          {user.locked ? (
            <Badge variant="outline" className="text-orange-600 border-orange-200 font-normal">
              <Lock className="size-3 mr-1" />
              Locked
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <RowActions user={user} />
      </TableCell>
    </TableRow>
  );
}

function UserStateBadge({ state }: { state: AdminUser["state"] }) {
  switch (state) {
    case "INVITED":
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-200">
          Invited
        </Badge>
      );
    case "INVITE_EXPIRED":
      return (
        <Badge variant="outline" className="text-destructive border-destructive/30">
          Invite expired
        </Badge>
      );
    case "ACTIVE":
      return (
        <Badge variant="outline" className="text-emerald-600 border-emerald-200">
          Active
        </Badge>
      );
    case "ARCHIVED":
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Archived
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground">
          {state}
        </Badge>
      );
  }
}

function UserTypeBadge({ type }: { type: AdminUser["userType"] }) {
  const variant =
    type === "DEFAULT" ? "default" : type === "AZURE" ? "secondary" : "outline";
  return (
    <Badge variant={variant} className="font-normal">
      {type}
    </Badge>
  );
}

// =====================================================================================
// Row actions
// =====================================================================================

type RowDialog = "reset" | "auth-type" | "archive" | "revoke-invite" | null;

function RowActions({ user }: { user: AdminUser }) {
  const [dialog, setDialog] = useState<RowDialog>(null);
  const [issuedLink, setIssuedLink] = useState<OneTimeLink | null>(null);

  const { mutate: resendInvite, isPending: resending } = useResendInvite();
  const { mutate: unarchive, isPending: unarchiving } = useUnarchiveUser();
  const { mutate: syncInstances, isPending: syncing } = useSyncUserInstances();
  const { toast } = useToast();

  const isDefault = user.userType === "DEFAULT";
  const canResendInvite =
    user.userType === "LOCAL" && (user.state === "INVITED" || user.state === "INVITE_EXPIRED");
  const canRevokeInvite = user.state === "INVITED";
  const canResetPassword =
    user.state === "ACTIVE" && (user.userType === "LOCAL" || user.userType === "DEFAULT");
  // DEFAULT admins have implicit access to all instances — nothing to sync.
  const canSync = !isDefault;
  const canSwitchAuthType = !isDefault && user.state !== "ARCHIVED";
  const canArchive = !isDefault && user.state !== "ARCHIVED";
  const canUnarchive = user.state === "ARCHIVED";

  const hasAnyAction =
    canResendInvite || canRevokeInvite || canResetPassword || canSync || canSwitchAuthType || canArchive || canUnarchive;

  if (!hasAnyAction) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const onResendInvite = () => {
    resendInvite(user.id, {
      onSuccess: (result) => {
        toast({ title: "Invite re-sent", description: `A fresh invite was issued for ${user.email}.` });
        if (result.inviteLink) {
          setIssuedLink({
            title: "Invite created",
            description: `One-time set-password link for ${user.email}.`,
            link: result.inviteLink,
            emailSent: result.emailSent,
            expiresInMinutes: result.expiresInMinutes,
          });
        }
      },
    });
  };

  const onSync = () => {
    syncInstances(user.id, {
      onSuccess: (result) => {
        toast({ title: "Instance access synced", description: describeSync(result.sync) });
      },
    });
  };

  const onUnarchive = () => {
    unarchive(user.id, {
      onSuccess: (result) => {
        if (result.inviteLink) {
          toast({
            title: "User unarchived",
            description: `${user.email} never set a password, so a fresh invite was issued.`,
          });
          setIssuedLink({
            title: "Invite created",
            description: `One-time set-password link for ${user.email}.`,
            link: result.inviteLink,
            emailSent: result.emailSent,
            expiresInMinutes: result.expiresInMinutes,
          });
        } else {
          toast({ title: "User unarchived", description: `${user.email} can sign in again.` });
        }
      },
    });
  };

  return (
    <>
      {/* modal={false}: a modal menu + modal dialog both toggle body pointer-events, and the
          restore races when a menu item opens a dialog — body gets stuck at "none" and the
          whole page freezes until refresh (radix-ui/primitives#1836). */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <MoreHorizontal className="size-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {canResendInvite ? (
            <DropdownMenuItem onClick={onResendInvite} disabled={resending}>
              {resending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Resend invite
            </DropdownMenuItem>
          ) : null}
          {canRevokeInvite ? (
            <DropdownMenuItem onClick={() => setDialog("revoke-invite")}>
              <MailX className="size-4" />
              Revoke invite
            </DropdownMenuItem>
          ) : null}
          {canResetPassword ? (
            <DropdownMenuItem onClick={() => setDialog("reset")}>
              <KeyRound className="size-4" />
              Reset password
            </DropdownMenuItem>
          ) : null}
          {canSync ? (
            <DropdownMenuItem onClick={onSync} disabled={syncing}>
              {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              Sync instance access
            </DropdownMenuItem>
          ) : null}
          {canSwitchAuthType ? (
            <DropdownMenuItem onClick={() => setDialog("auth-type")}>
              <ShieldCheck className="size-4" />
              Switch to {user.userType === "LOCAL" ? "Azure" : "Local"}
            </DropdownMenuItem>
          ) : null}
          {canUnarchive ? (
            <DropdownMenuItem onClick={onUnarchive} disabled={unarchiving}>
              {unarchiving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ArchiveRestore className="size-4" />
              )}
              Unarchive
            </DropdownMenuItem>
          ) : null}
          {canArchive ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDialog("archive")}
              >
                <Archive className="size-4" />
                Archive
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialog === "reset"} onOpenChange={(o) => !o && setDialog(null)}>
        {dialog === "reset" ? (
          <ResetPasswordDialog
            user={user}
            onDone={() => setDialog(null)}
            onLinkIssued={(link) => {
              setDialog(null);
              setIssuedLink(link);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog open={dialog === "auth-type"} onOpenChange={(o) => !o && setDialog(null)}>
        {dialog === "auth-type" ? (
          <ChangeAuthTypeDialog
            user={user}
            onDone={() => setDialog(null)}
            onLinkIssued={(link) => {
              setDialog(null);
              setIssuedLink(link);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog open={dialog === "archive"} onOpenChange={(o) => !o && setDialog(null)}>
        {dialog === "archive" ? <ArchiveUserDialog user={user} onDone={() => setDialog(null)} /> : null}
      </Dialog>
      <Dialog open={dialog === "revoke-invite"} onOpenChange={(o) => !o && setDialog(null)}>
        {dialog === "revoke-invite" ? (
          <RevokeInviteDialog user={user} onDone={() => setDialog(null)} />
        ) : null}
      </Dialog>

      <OneTimeLinkDialog link={issuedLink} onClose={() => setIssuedLink(null)} />
    </>
  );
}

function ResetPasswordDialog({
  user,
  onDone,
  onLinkIssued,
}: {
  user: AdminUser;
  onDone: () => void;
  onLinkIssued: (link: OneTimeLink) => void;
}) {
  const { mutate, isPending } = useResetPassword();
  const { toast } = useToast();

  const submit = () => {
    mutate(user.id, {
      onSuccess: (result) => {
        toast({ title: "Reset link issued", description: `One-time reset link for ${user.email}.` });
        onLinkIssued({
          title: "Reset link created",
          description: `One-time password-reset link for ${user.email}.`,
          link: result.resetLink,
          emailSent: result.emailSent,
          expiresInMinutes: result.expiresInMinutes,
        });
      },
    });
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Reset password</DialogTitle>
        <DialogDescription>
          Issue a one-time reset link for {user.email}. They set their own new password — you never
          see it. Any previous reset link stops working.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
          Create reset link
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ChangeAuthTypeDialog({
  user,
  onDone,
  onLinkIssued,
}: {
  user: AdminUser;
  onDone: () => void;
  onLinkIssued: (link: OneTimeLink) => void;
}) {
  const target = user.userType === "LOCAL" ? "AZURE" : "LOCAL";
  const { mutate, isPending } = useChangeAuthType();
  const { toast } = useToast();

  const submit = () => {
    mutate(
      { id: user.id, newType: target },
      {
        onSuccess: (result: AdminUserWithLink) => {
          toast({ title: "Auth type changed", description: `${user.email} is now ${target}.` });
          if (result.inviteLink) {
            onLinkIssued({
              title: "Invite created",
              description: `One-time set-password link for ${user.email}.`,
              link: result.inviteLink,
              emailSent: result.emailSent,
              expiresInMinutes: result.expiresInMinutes,
            });
          } else {
            onDone();
          }
        },
      }
    );
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Switch to {target === "AZURE" ? "Azure (SSO)" : "Local"}</DialogTitle>
        <DialogDescription>
          {target === "AZURE"
            ? "The stored password is cleared; the user will sign in via Azure SSO."
            : "The user will receive an invite link to set their password."}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Confirm
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ArchiveUserDialog({ user, onDone }: { user: AdminUser; onDone: () => void }) {
  const { mutate, isPending } = useArchiveUser();
  const { toast } = useToast();

  const submit = () => {
    mutate(user.id, {
      onSuccess: () => {
        toast({ title: "User archived", description: `${user.email} can no longer sign in.` });
        onDone();
      },
    });
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Archive user</DialogTitle>
        <DialogDescription>
          {user.email} will be unable to sign in and any pending invite or reset links stop
          working. Their access mappings are kept — you can unarchive them later.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={submit} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Archive
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function RevokeInviteDialog({ user, onDone }: { user: AdminUser; onDone: () => void }) {
  const { mutate, isPending } = useRevokeInvite();
  const { toast } = useToast();

  const submit = () => {
    mutate(user.id, {
      onSuccess: () => {
        toast({ title: "Invite revoked", description: `The invite link for ${user.email} no longer works.` });
        onDone();
      },
    });
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Revoke invite</DialogTitle>
        <DialogDescription>
          The pending invite link for {user.email} stops working immediately. You can resend a
          fresh invite at any time.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={submit} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Revoke
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// =====================================================================================
// Add user (lookup → create → invite link)
// =====================================================================================

function AddUserDialog({ onDone }: { onDone: () => void }) {
  const [searchField, setSearchField] = useState<LookupField>("email");
  const [query, setQuery] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userType, setUserType] = useState<"LOCAL" | "AZURE">("LOCAL");
  /** After a successful LOCAL create the dialog switches to the invite-link result step. */
  const [invite, setInvite] = useState<AdminUserWithLink | null>(null);

  const { mutate: lookup, data: result, isPending: looking } = useUserLookup();
  const { mutate: create, isPending: creating } = useCreateUser();
  const { toast } = useToast();

  // Mirrors the backend verification gate: migrations need an accessible source AND target, so
  // a user matched on fewer than two active instances cannot be created.
  const hasEnoughMatches = !!result && result.matchedInstances.length >= 2;
  const fieldConfig = LOOKUP_FIELDS.find((f) => f.value === searchField)!;

  // Prefill the create form from the first match once a lookup succeeds.
  useEffect(() => {
    if (!result || result.matchedInstances.length === 0) return;
    const m = result.matchedInstances[0];
    setEmail(m.remoteEmail ?? "");
    setUsername(m.remoteUsername ?? "");
    setEmployeeId(m.remoteEmployeeId ?? "");
    setFirstName(m.firstName ?? "");
    setLastName(m.lastName ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const onSearch = () => {
    if (!query.trim()) {
      toast({
        variant: "destructive",
        title: "Identify the user",
        description: `Enter the ${fieldConfig.label.toLowerCase()} to search.`,
      });
      return;
    }
    lookup({ query: query.trim(), field: searchField });
  };

  const onCreate = () => {
    if (!email.trim()) {
      toast({ variant: "destructive", title: "An email is required to create the user" });
      return;
    }
    create(
      {
        email: email.trim(),
        username: username.trim() || undefined,
        employeeId: employeeId.trim() || undefined,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        userType,
        searchField,
      },
      {
        onSuccess: (u) => {
          toast({
            title: "User created",
            description: `${u.email} added with access to ${u.instanceIds?.length ?? 0} instance(s).`,
          });
          if (u.inviteLink) {
            // LOCAL create: show the one-time invite link inside the dialog.
            setInvite(u);
          } else {
            // AZURE create: immediately active, nothing more to hand out.
            onDone();
          }
        },
      }
    );
  };

  if (invite?.inviteLink) {
    return (
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite created</DialogTitle>
          <DialogDescription>
            {invite.email} was created and invited. They set their own password via this one-time
            link.
          </DialogDescription>
        </DialogHeader>
        <OneTimeLinkPanel
          link={invite.inviteLink}
          emailSent={invite.emailSent}
          expiresInMinutes={invite.expiresInMinutes}
        />
        <DialogFooter>
          <Button onClick={onDone}>Done</Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Add user</DialogTitle>
        <DialogDescription>
          Look the person up on a connected Leucine instance, then create their MU account. Local
          users receive an invite link to set their own password.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="space-y-2">
            <Label htmlFor="lookup-field">Search by</Label>
            <Select
              value={searchField}
              onValueChange={(v) => {
                setSearchField(v as LookupField);
                setQuery("");
              }}
            >
              <SelectTrigger id="lookup-field" className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOOKUP_FIELDS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-2">
            <Label htmlFor="lookup-query">{fieldConfig.label}</Label>
            <Input
              id="lookup-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={fieldConfig.placeholder}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
            />
          </div>
          <Button onClick={onSearch} disabled={looking} variant="outline">
            {looking ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
            Search
          </Button>
        </div>

        {result ? <LookupResultPanel result={result} /> : null}

        {hasEnoughMatches ? (
          <div className="space-y-4 border-t pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first-name">First name</Label>
                <Input id="first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last name</Label>
                <Input id="last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-type">Sign-in method</Label>
              <Select value={userType} onValueChange={(v) => setUserType(v as "LOCAL" | "AZURE")}>
                <SelectTrigger id="user-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOCAL">Local (password)</SelectItem>
                  <SelectItem value="AZURE">Azure (SSO)</SelectItem>
                </SelectContent>
              </Select>
              {userType === "LOCAL" ? (
                <p className="text-xs text-muted-foreground">
                  The user receives a one-time invite link and sets their own password.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!hasEnoughMatches || creating} onClick={onCreate}>
          {creating ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
          Create user
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function LookupResultPanel({ result }: { result: UserLookupResult }) {
  if (result.matchedInstances.length === 0) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
        <div className="flex items-center gap-2 text-destructive">
          <XCircle className="size-4" />
          <span className="font-medium">No matches found</span>
        </div>
        <p className="mt-1 text-muted-foreground">
          Users must be present on at least two active Leucine instances before they can be added
          to the MU. Verify the email or username is correct.
        </p>
      </div>
    );
  }

  // A single match isn't enough: migrations need an accessible source AND target, so the
  // backend rejects creation below two instances. Show why instead of a green check.
  const enoughMatches = result.matchedInstances.length >= 2;

  return (
    <div className={`rounded-md border p-3 ${enoughMatches ? "bg-card" : "border-amber-500/40 bg-amber-500/5"}`}>
      <div className="flex items-center gap-2 mb-2">
        {enoughMatches ? (
          <CheckCircle2 className="size-4 text-emerald-600" />
        ) : (
          <AlertTriangle className="size-4 text-amber-600" />
        )}
        <span className="text-sm font-medium">
          Matched on {result.matchedInstances.length} instance
          {result.matchedInstances.length === 1 ? "" : "s"}
        </span>
      </div>
      {!enoughMatches && (
        <p className="mb-2 text-sm text-muted-foreground">
          This user can't be added yet: migrations need access to both a source and a target, so
          users must be present on at least two active instances.
        </p>
      )}
      <ul className="space-y-2 text-sm">
        {result.matchedInstances.map((m) => (
          <li key={m.instanceId} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-normal">
                {m.instanceName}
              </Badge>
              <span className="text-xs text-muted-foreground">matched by {m.matchedBy}</span>
            </div>
            <span className="text-xs font-mono text-muted-foreground">
              {m.remoteEmail || m.remoteEmployeeId || m.remoteUserId}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
